import { useMemo, useEffect } from "react"

import { getIn, useFormikContext } from "formik"
import * as Yup from 'yup'

import { FormikFileUpload, FormikSelect, FormikNotesArea } from "../../../../../../components/widgets"
import { DOCModelsSchema, IDLMWebDOCModel, createDLMDOC } from "../../../../../core/doclabel"

import { useDLMResponseFormContext } from "../../components"

import { DLMGenerateDOCButton } from "../components"

import { getErrorMessage } from "../../../../../../components/helpers"

import { useUserLoader } from "../../../../../core/users"
import { IWebFileAttachment } from "../../../../../core/applications"
import { sortBy } from "lodash"

// Site Inspection
export interface IDLMProgramManagerSetup  {
  verdict: string
  workflow: string
  pmID: number
  docModels: Array<IDLMWebDOCModel>
  note: string
  noteAttachments: Array<IWebFileAttachment>
}

export const initDLMProgramManagerSetupValues = {
  verdict: '',
  workflow: '',
  pmID: 0,
  docModels: [],
  note: '',
  noteAttachments: [],  
}

export const DLMProgramManagerSetupSchema: any = Yup.object({
  appState: Yup.string().trim(),
  verdict: Yup.string().trim(),    
  workflow: Yup.string().trim(),
  pmID: Yup.number().integer().label('Program Manager')
  .when('appState', {
    is: (val: string) => (! ['Program Manager Rejected','Program Manager Rejected (A)'].includes(val)),
    then: (schema) => schema.required().moreThan(0, 'Select Program Manager'),
  }),
  docModels: Yup.array().label("DOC Attachment")        
  .when(['appState', 'verdict'], {
    is: (appState: string, verdict: string) => (! (['Program Manager Rejected','Program Manager Rejected (A)'].includes(appState) && verdict === 'rejected')),
    then: (schema) => schema.of(DOCModelsSchema).required().min(1, 'DOC Attachment required'),
  }),
  note: Yup.string().trim().label('Note').required(),
  noteAttachments: Yup.array(),  
});

export const DLMProgramManagerSetupForm = () => {

  const { appID, appState } = useDLMResponseFormContext();
  const { values, setFieldValue, validateField } = useFormikContext();

  const { verdict, workflow, docModels } = useMemo(() => {
    const verdict = getIn(values, 'verdict');    
    const workflow = getIn(values, 'workflow');    
    const docModels = getIn(values, 'docModels');    

    return { verdict, workflow, docModels };
  }, [values]);


  const { data: programManagers } = useUserLoader({userType: (workflow && workflow === 'MandatoryDOC' && (! ['Program Manager Rejected','Program Manager Rejected (A)'].includes(appState)))  ? "DLM Program Manager" : ''});

  const setDOCModel = (vals: any, idx: number) => {
    if (! (vals && typeof(vals) === 'object' && vals?.docId)) return;

    // clear attachment
    Object.assign(vals, {
      docAttachmentId: 0,
      docAttachmentIdAttachmentName: ''
    });

    setFieldValue(`docModels.${idx}`,vals, true);        
  }

  useEffect(() => {
    if (! (appID)) {
      return;
    }

    if (verdict === 'rejected') {
      setFieldValue('docModels', []);
      return;
    }

    if (workflow === 'DOCEdit' || workflow === 'DOCSplit' || workflow === 'MandatoryDOC' || (workflow === 'DOC' && ['Program Manager Rejected','Program Manager Rejected (A)'].includes(appState))) {
      createDLMDOC(appID)
      .then( res => {
        if (! (res.data && res.data.length > 0)) {
          console.error('Something happened');
          return;
        }
        setFieldValue('docModels', res.data);        
      })
      .catch( err => {
        console.error(getErrorMessage(err));
      })
    }
  // eslint-disable-next-line
  },[appID, workflow, appState, verdict]);

  
  return (
  <>
  { ! (['Program Manager Rejected','Program Manager Rejected (A)'].includes(appState)) && 
  <div className="row mb-4">        
    <FormikSelect size="6" label="Assign Program Manager" name="pmID" required={true} options={programManagers} placeholder='Select Program Manager' />
  </div>        
  }

  { ((! (['Program Manager Rejected','Program Manager Rejected (A)'].includes(appState) && verdict !== 'approved')) && (docModels && docModels.length > 0)) && 
  sortBy(docModels, 'cbRef').map((docModel: any, idx: number) => (
  <div key={`setDOCrow_${idx}`} className="row">
    <div className="col-4 py-2 mt-1">
      <DLMGenerateDOCButton index={idx} onGenDOC={setDOCModel} baseData={docModel} />
    </div>           
    <FormikFileUpload 
      size="8" 
      label={`${docModel?.cbRef ? docModel.cbRef : docModel.projectName} - DOC Attachment`} 
      name={`docModels.${idx}.docAttachmentId`} 
      acceptFiles="UserFileFormats" 
      required={true} 
      disabled={(docModel?.docId) ? false : true} 
      subTitle={(docModel?.docId) ? "Upload DOC here" : "Generate DOC first."}
      onUpdateFunc={() => {
        setTimeout(() => {
          validateField('docModels');                    
        }, 100)                 
      }}
    />
  </div>
  ))        
  }     
  <FormikNotesArea required />
  </>
  )
}